<template>
  <div class="verification-code">
    <div class="verification-code__header">
      <p class="verification-code__email">
        {{ $t('Login.verificationCode.emailSent', { email }) }}
      </p>
    </div>

    <div class="verification-code__inputs">
      <div
        v-for="(digit, index) in displayDigits"
        :key="index"
        :class="[
          'verification-code__box',
          { 'verification-code__box--focused': focusedIndex === index },
          { 'verification-code__box--error': hasError },
          { 'verification-code__box--filled': !!digit }
        ]">
        {{ digit }}
      </div>
      <input
        ref="realInput"
        v-model="codeValue"
        type="text"
        inputmode="numeric"
        pattern="[0-9]*"
        maxlength="6"
        autocomplete="one-time-code"
        class="verification-code__real-input"
        :disabled="disabled"
        :aria-label="$t('Login.verificationCode.digitLabel', { digit: 1 })"
        @input="handleInput"
        @keydown="handleKeydown"
        @focus="isFocused = true"
        @blur="isFocused = false" />
    </div>

    <div v-if="hasError" class="verification-code__error">
      <p class="verification-code__error-text">{{ errorMessage }}</p>
    </div>

    <div class="verification-code__resend">
      <AtomButton
        v-if="canResend"
        appearance="unstyled"
        class="function-link"
        :disabled="isResending || disabled"
        @click="handleResend">
        <template #text>
          {{
            isResending
              ? $t('Login.verificationCode.sending')
              : $t('Login.verificationCode.sendNew')
          }}
        </template>
      </AtomButton>
      <p v-else class="verification-code__countdown">
        {{ $t('Login.verificationCode.countdown', { seconds: countdown }) }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
const { t } = useI18n();

interface Props {
  email?: string;
  modelValue?: string;
  error?: string;
  disabled?: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: string): void;
  (e: 'complete', value: string): void;
  (e: 'resend'): void;
}

const props = withDefaults(defineProps<Props>(), {
  email: '',
  modelValue: '',
  error: '',
  disabled: false
});

const emit = defineEmits<Emits>();

const realInput = ref<HTMLInputElement | null>(null);
const codeValue = ref('');
const isFocused = ref(false);
const countdown = ref<number>(10);
const canResend = ref<boolean>(false);
const isResending = ref<boolean>(false);
const countdownInterval = ref<NodeJS.Timeout | null>(null);

const hasError = computed(() => !!props.error);
const errorMessage = computed(
  () => props.error || t('Login.verificationCode.invalidCode')
);

const displayDigits = computed(() => {
  return Array.from({ length: 6 }, (_, i) => codeValue.value[i] || '');
});

const focusedIndex = computed(() => {
  if (!isFocused.value) return -1;
  return Math.min(codeValue.value.length, 5);
});

// Sync external modelValue changes
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== codeValue.value) {
      codeValue.value = newValue.replace(/[^\d]/g, '').slice(0, 6);
    }
  },
  { immediate: true }
);

// Emit on internal changes
watch(codeValue, (newValue) => {
  if (newValue !== props.modelValue) {
    emit('update:modelValue', newValue);
  }
  if (newValue.length === 6 && /^\d{6}$/.test(newValue)) {
    emit('complete', newValue);
  }
});

function handleInput() {
  // Strip non-digits and cap at 6
  codeValue.value = codeValue.value.replace(/[^\d]/g, '').slice(0, 6);
}

function handleKeydown(event: KeyboardEvent) {
  // Allow: backspace, delete, tab, enter, arrows, home, end, paste shortcuts
  const allowed = [
    'Backspace',
    'Delete',
    'Tab',
    'Enter',
    'ArrowLeft',
    'ArrowRight',
    'Home',
    'End'
  ];
  if (allowed.includes(event.key)) return;
  if (event.metaKey || event.ctrlKey) return;

  // Block non-digit keys
  if (!/^\d$/.test(event.key)) {
    event.preventDefault();
  }
}

function handleResend() {
  if (!canResend.value || isResending.value) return;

  isResending.value = true;
  clearCode();
  emit('resend');

  setTimeout(() => {
    isResending.value = false;
    startCountdown();
  }, 1000);
}

function startCountdown() {
  canResend.value = false;
  countdown.value = 10;

  countdownInterval.value = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      canResend.value = true;
      if (countdownInterval.value) {
        clearInterval(countdownInterval.value);
      }
    }
  }, 1000);
}

function clearCode() {
  codeValue.value = '';
  realInput.value?.focus();
}

onMounted(() => {
  startCountdown();
  nextTick(() => {
    realInput.value?.focus();
  });
});

onUnmounted(() => {
  if (countdownInterval.value) {
    clearInterval(countdownInterval.value);
  }
});

defineExpose({
  clearCode,
  focusInput: () => realInput.value?.focus()
});
</script>

<style lang="scss" scoped>
.verification-code {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  &__header {
    margin-bottom: 2rem;
    text-align: center;
  }

  &__email {
    color: $color-gray-500;
    margin: 0;
  }

  &__inputs {
    position: relative;
    display: flex;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
    justify-content: center;

    @media (max-width: 480px) {
      gap: 0.5rem;
    }
  }

  &__box {
    width: 4rem;
    height: 5rem;
    border: 1px solid $color-gray-300;
    border-radius: $theme-border-radius;
    box-shadow: $theme-box-shadow;
    font-size: font-size('sm');
    font-weight: font-weight('medium');
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    transition: border-color 0.2s ease;
    pointer-events: none;

    @media (max-width: 480px) {
      width: 2.5rem;
      height: 3rem;
      font-size: font-size('xs');
    }

    &--focused {
      border-color: $color-gray-600;
    }

    &--error {
      border-color: $color-red-700;
      background-color: $color-red-100;
    }
  }

  &__real-input {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    font-size: font-size('sm');
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
    }
  }

  &__error {
    margin-bottom: 1rem;
    min-height: 1.25rem;
  }

  &__error-text {
    color: $color-red-700;
    margin: 0;
    text-align: center;
  }

  &__resend {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 2.5rem;
  }

  &__countdown {
    color: $color-gray-500;
    margin: 0;
  }
}
</style>
